import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "bg-white w-full text-gray-600 divide-y" }
const _hoisted_3 = { class: "text-black" }
const _hoisted_4 = { class: "p-6 text-left font-normal" }
const _hoisted_5 = { class: "p-6 text-left font-normal" }
const _hoisted_6 = { class: "p-6 text-left font-normal" }
const _hoisted_7 = {
  key: 0,
  class: "p-6 text-left font-normal"
}
const _hoisted_8 = { class: "p-6 text-left font-normal" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("th", { class: "p-6 text-left font-normal" }, null, -1)
const _hoisted_10 = { class: "p-6" }
const _hoisted_11 = { class: "p-6" }
const _hoisted_12 = { class: "p-6" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_15 = {
  key: 0,
  class: "p-6"
}
const _hoisted_16 = { class: "p-6" }
const _hoisted_17 = { class: "p-6" }
const _hoisted_18 = {
  key: 0,
  class: "mt-6"
}
const _hoisted_19 = ["src", "title"]

import { computed, onBeforeUnmount, ref } from "vue";
import { DocumentDataService } from "@/services";
import { t } from "@/i18n";
import { formatDateTime } from "@/utils/date";
import PdfViewer from "@/components/common/PdfViewer.vue";


export default /*#__PURE__*/_defineComponent({
  props: {
    file: null,
    documentId: null
  },
  setup(__props: any) {

const props = __props as {
  file: {
    creator: string;
    filename: string;
    description: string;
    createdAt: string;
    fileGuid: string;
    fileType: string;
    fileExt?: string;
    isElectronicCopy?: boolean;
  };
  documentId?: string | null;
}

const fileBlob = ref<Blob | null>(null);
const mobilePreviewOpen = ref(false);
const desktopPreviewVisible = ref(false);
const desktopPreviewUrl = ref("");
const previewLoading = ref(false);
const publicSignsUrl = ref<string | null>(null);
const previewableMimeTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];


const hasDescription = computed(() => Boolean(props.file.description?.trim()));

const resolveQrUrl = async () => {
  if (publicSignsUrl.value) {
    return publicSignsUrl.value;
  }
  if (props.documentId == null || props.documentId === "") {
    return null;
  }
  try {
    const response = await DocumentDataService.getPublicSignsUrl(
      props.documentId
    );
    const url = response?.data?.data?.url;
    if (url) {
      publicSignsUrl.value = url;
      return url;
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

const loadFileBlob = async () => {
  if (fileBlob.value) return fileBlob.value;
  const qrUrl = props.file.isElectronicCopy ? null : await resolveQrUrl();
  const response = await DocumentDataService.downloadDocument(
    props.file.fileGuid,
    qrUrl
  );
  const contentDisposition = String(
    response.headers?.["content-disposition"] || ""
  ).toLowerCase();
  const responseIsPdf =
    contentDisposition.includes(".pdf") ||
    sourceExtension.value === ".pdf" ||
    props.file.fileType === "application/pdf" ||
    (Boolean(qrUrl) &&
      [".doc", ".docx"].includes(sourceExtension.value));
  fileBlob.value = new Blob([response.data], {
    type: responseIsPdf
      ? "application/pdf"
      : props.file.fileType || "application/octet-stream",
  });
  return fileBlob.value;
};

const showFile = async () => {
  if (previewLoading.value) return;
  previewLoading.value = true;
  try {
    const blob = await loadFileBlob();
    if (isMobilePreview()) {
      desktopPreviewVisible.value = false;
      mobilePreviewOpen.value = true;
      return;
    }
    mobilePreviewOpen.value = false;
    if (!desktopPreviewUrl.value) {
      desktopPreviewUrl.value = URL.createObjectURL(blob);
    }
    desktopPreviewVisible.value = !desktopPreviewVisible.value;
  } catch (error) {
    console.error(error);
  } finally {
    previewLoading.value = false;
  }
};

const closePreview = () => {
  mobilePreviewOpen.value = false;
};

const isMobilePreview = () =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
  window.matchMedia("(max-width: 768px)").matches;

const sourceExtension = computed(() => {
  const fileExt = (props.file.fileExt || "").toLowerCase();
  if (fileExt) {
    return fileExt.startsWith(".") ? fileExt : `.${fileExt}`;
  }
  const extensionMatch = (props.file.filename || "").match(/\.[^.]+$/);
  return extensionMatch?.[0]?.toLowerCase() || "";
});

const canPreview = computed(
  () =>
    Boolean(props.file.fileGuid) &&
    (previewableMimeTypes.includes(props.file.fileType) ||
      [".pdf", ".doc", ".docx"].includes(sourceExtension.value))
);

const downloadDocument = async () => {
  try {
    const blob = await loadFileBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = props.file.filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error(error);
  }
};

onBeforeUnmount(() => {
  mobilePreviewOpen.value = false;
  if (desktopPreviewUrl.value) URL.revokeObjectURL(desktopPreviewUrl.value);
  fileBlob.value = null;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: "table-scroll",
      style: {"--table-min-width":"900px"},
      tabindex: "0",
      role: "region",
      "aria-label": _ctx.$t('common.scrollableTable')
    }, [
      _createElementVNode("table", _hoisted_2, [
        _createElementVNode("tr", _hoisted_3, [
          _createElementVNode("th", _hoisted_4, _toDisplayString(_unref(t)("fileTable.owner")), 1),
          _createElementVNode("th", _hoisted_5, _toDisplayString(_unref(t)("fileTable.fileName")), 1),
          _createElementVNode("th", _hoisted_6, _toDisplayString(_unref(t)("fileTable.content")), 1),
          (_unref(hasDescription))
            ? (_openBlock(), _createElementBlock("th", _hoisted_7, _toDisplayString(_unref(t)("fileTable.description")), 1))
            : _createCommentVNode("", true),
          _createElementVNode("th", _hoisted_8, _toDisplayString(_unref(t)("fileTable.uploadedAt")), 1),
          _hoisted_9
        ]),
        _createElementVNode("tr", null, [
          _createElementVNode("td", _hoisted_10, _toDisplayString(__props.file.creator), 1),
          _createElementVNode("td", _hoisted_11, _toDisplayString(__props.file.filename), 1),
          _createElementVNode("td", _hoisted_12, [
            (_unref(canPreview))
              ? (_openBlock(), _createElementBlock("a", {
                  key: 0,
                  href: "#",
                  onClick: _withModifiers(showFile, ["prevent"]),
                  class: "no-underline text-green-600"
                }, _toDisplayString(_unref(t)("fileTable.showDocument")), 9, _hoisted_13))
              : (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(_unref(t)("fileTable.previewUnavailable")), 1))
          ]),
          (_unref(hasDescription))
            ? (_openBlock(), _createElementBlock("td", _hoisted_15, _toDisplayString(__props.file.description), 1))
            : _createCommentVNode("", true),
          _createElementVNode("td", _hoisted_16, _toDisplayString(_unref(formatDateTime)(__props.file.createdAt)), 1),
          _createElementVNode("td", _hoisted_17, [
            _createElementVNode("a", {
              href: "#",
              onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (downloadDocument()), ["prevent"])),
              target: "_blank",
              class: "no-underline text-green-600"
            }, _toDisplayString(_unref(t)("fileTable.downloadFile")), 1)
          ])
        ])
      ])
    ], 8, _hoisted_1),
    (desktopPreviewVisible.value && desktopPreviewUrl.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
          _createElementVNode("iframe", {
            width: "100%",
            height: "1000px",
            src: desktopPreviewUrl.value,
            title: __props.file.filename
          }, null, 8, _hoisted_19)
        ]))
      : _createCommentVNode("", true),
    _createVNode(PdfViewer, {
      show: mobilePreviewOpen.value,
      source: fileBlob.value,
      title: __props.file.filename,
      onClose: closePreview
    }, null, 8, ["show", "source", "title"])
  ], 64))
}
}

})